/**
 * QuickBill Receipt Template Engine
 * Handles multiple text-based receipt templates for thermal printers
 * 
 * Features:
 * - Multiple template types (compact, detailed, columnized)
 * - Support for 58mm and 80mm paper widths
 * - GST-compliant formatting
 * - Multilingual support (via image generation)
 * - Default printer fonts only
 * 
 * @author QuickBill Team
 * @version 1.0.0
 */

import { ESCPOSCommandBuilder } from './printerUtility'
import { defaultSanitizer, sanitizeText } from './characterSanitizer'
import { defaultTextImageConverter, shouldConvertToImage } from './textImageConverter'

/**
 * Template configuration and metadata
 */
export const TEMPLATE_CONFIGS = {
  'compact-58': {
    id: 'compact-58',
    name: 'Compact (58mm)',
    description: 'Minimal space-efficient receipt',
    paperWidth: 58,
    charWidth: 32,
    category: 'compact',
    preview: 'Simple format with essential info only',
    features: ['Space-efficient', 'Essential info only', 'Quick print'],
    bestFor: 'Fast checkout, small receipts, basic transactions'
  },
  'compact-80': {
    id: 'compact-80',
    name: 'Compact (80mm)',
    description: 'Minimal space-efficient receipt',
    paperWidth: 80,
    charWidth: 48,
    category: 'compact',
    preview: 'Simple format with essential info only',
    features: ['Space-efficient', 'Essential info only', 'Quick print', 'Wide format'],
    bestFor: 'Fast checkout, readable text, basic transactions'
  },
  'detailed-58': {
    id: 'detailed-58',
    name: 'Detailed (58mm)',
    description: 'Complete business receipt with GST details',
    paperWidth: 58,
    charWidth: 32,
    category: 'detailed',
    preview: 'Full business info, customer details, GST breakdown',
    features: ['Business details', 'Customer info', 'GST breakdown', 'Complete receipt'],
    bestFor: 'GST businesses, detailed records, compliance'
  },
  'detailed-80': {
    id: 'detailed-80',
    name: 'Detailed (80mm)',
    description: 'Complete business receipt with GST details',
    paperWidth: 80,
    charWidth: 48,
    category: 'detailed',
    preview: 'Full business info, customer details, GST breakdown',
    features: ['Business details', 'Customer info', 'GST breakdown', 'Complete receipt', 'Wide format'],
    bestFor: 'GST businesses, professional appearance, compliance'
  },
  'columnized-58': {
    id: 'columnized-58',
    name: 'Columnized (58mm)',
    description: 'Table format with aligned columns',
    paperWidth: 58,
    charWidth: 32,
    category: 'columnized',
    preview: 'Professional table layout with columns',
    features: ['Table format', 'Aligned columns', 'Professional layout', 'MRP display'],
    bestFor: 'Retail stores, item comparison, professional receipts'
  },
  'columnized-80': {
    id: 'columnized-80',
    name: 'Columnized (80mm)',
    description: 'Table format with aligned columns',
    paperWidth: 80,
    charWidth: 48,
    category: 'columnized',
    preview: 'Professional table layout with columns',
    features: ['Table format', 'Aligned columns', 'Professional layout', 'MRP display', 'Wide format'],
    bestFor: 'Retail stores, supermarkets, professional appearance'
  }
}

/**
 * Utility functions for text formatting
 */
class TextFormatter {
  /**
   * Pad text to center within given width
   */
  static center(text, width) {
    if (text.length >= width) return text.substring(0, width)
    const padding = Math.floor((width - text.length) / 2)
    return ' '.repeat(padding) + text + ' '.repeat(width - text.length - padding)
  }

  /**
   * Pad text to right align within given width
   */
  static right(text, width) {
    if (text.length >= width) return text.substring(0, width)
    return ' '.repeat(width - text.length) + text
  }

  /**
   * Pad text to left align within given width
   */
  static left(text, width) {
    if (text.length >= width) return text.substring(0, width)
    return text + ' '.repeat(width - text.length)
  }

  /**
   * Create a line with left and right text
   */
  static leftRight(leftText, rightText, width, fillChar = ' ') {
    const left = leftText.toString()
    const right = rightText.toString()
    const availableSpace = width - left.length - right.length
    
    if (availableSpace >= 0) {
      return left + fillChar.repeat(availableSpace) + right
    } else {
      // If content is too long, truncate left text
      const maxLeftLength = width - right.length - 1
      return left.substring(0, maxLeftLength) + fillChar + right
    }
  }

  /**
   * Word wrap text to fit within width
   */
  static wordWrap(text, width) {
    if (text.length <= width) return [text]
    
    const words = text.split(' ')
    const lines = []
    let currentLine = ''

    for (const word of words) {
      if (currentLine.length + word.length + 1 <= width) {
        currentLine += (currentLine ? ' ' : '') + word
      } else {
        if (currentLine) lines.push(currentLine)
        currentLine = word
      }
    }

    if (currentLine) lines.push(currentLine)
    return lines
  }

  /**
   * Create separator line
   */
  static separator(char = '-', width = 32) {
    return char.repeat(width)
  }

  /**
   * Format currency
   */
  static currency(amount) {
    return `Rs.${parseFloat(amount).toFixed(2)}`
  }

  /**
   * Format date for receipt
   */
  static date(date) {
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Format address object into readable string
   */
  static formatAddress(address) {
    if (!address || typeof address !== 'object') {
      return ''
    }
    
    const addressParts = []
    if (address.street) addressParts.push(address.street)
    if (address.city) addressParts.push(address.city)
    if (address.state) {
      let statePart = address.state
      if (address.pincode) statePart += ` - ${address.pincode}`
      addressParts.push(statePart)
    } else if (address.pincode) {
      addressParts.push(address.pincode)
    }
    
    return addressParts.join(', ')
  }
}

/**
 * Base template class
 */
class BaseTemplate {
  constructor(config) {
    this.config = config
    this.charWidth = config.charWidth
    this.paperWidth = config.paperWidth
  }

  /**
   * Generate receipt commands
   */
  async generate(transaction, business, options = {}) {
    const builder = new ESCPOSCommandBuilder({
      paperWidth: this.paperWidth
    })

    builder.reset()

    // Build receipt sections
    await this.addHeader(builder, business)
    await this.addCustomerInfo(builder, transaction)
    await this.addItems(builder, transaction, business)
    await this.addTotals(builder, transaction, business)
    await this.addFooter(builder, business)

    // Cut paper if enabled
    if (options.cutPaper !== false) {
      builder.feed(3).cut()
    }

    return builder.build()
  }

  /**
   * Methods to be overridden by specific templates
   */
  async addHeader(builder, business) {
    throw new Error('addHeader method must be implemented by template')
  }

  async addCustomerInfo(builder, transaction) {
    throw new Error('addCustomerInfo method must be implemented by template')
  }

  async addItems(builder, transaction, business) {
    throw new Error('addItems method must be implemented by template')
  }

  async addTotals(builder, transaction, business) {
    throw new Error('addTotals method must be implemented by template')
  }

  async addFooter(builder, business) {
    throw new Error('addFooter method must be implemented by template')
  }

  /**
   * Check if text needs image generation (contains non-ASCII characters)
   */
  needsImageGeneration(text) {
    return /[^\u0000-\u007F]/.test(text)
  }

  /**
   * Add item name as image (with automatic fallback to sanitized text)
   * Always tries image generation first, falls back to text if it fails
   */
  async addItemName(builder, itemName) {
    try {
      // Validate item name input
      if (!itemName || typeof itemName !== 'string') {
        console.warn(`⚠️ Invalid item name provided:`, itemName, 'Using fallback')
        itemName = 'Item'
      }
      
      const name = itemName.trim()
      if (!name) {
        console.warn(`⚠️ Empty item name after trim, using fallback`)
        itemName = 'Item'
      }
      

      
      // Use compact configuration for thermal printing
      const imageCommands = await defaultTextImageConverter.textToImageCommands(itemName, {
        fontSize: 26, // Increased to match working business name size (from 20)
        maxWidth: (this.charWidth - 2) * 8, // Slightly wider
        wordWrap: true,
        padding: 2, // Reduced padding for compact output (from default 8)
        lineHeight: 1.2, // Tighter line spacing (from default 1.6)
        compactMode: true // Custom flag for compact rendering
      })
      

      
      // Flatten image commands properly (like working custom test)
      let imageCommandsFlat = []
      for (const command of imageCommands) {
        if (Array.isArray(command)) {
          imageCommandsFlat.push(...command)  // Spread array elements
        }
      }
      
      if (imageCommandsFlat.length > 0) {
        // Ultra compact image sequence - no extra feeds
        const initCommands = [] // No initialization for more compact output
        const feedCommands = [] // No line feeds - let template control spacing
        
        // Create complete command sequence exactly like working test
        const finalCommands = new Uint8Array(initCommands.length + imageCommandsFlat.length + feedCommands.length)
        let offset = 0
        
        // Add initialization
        finalCommands.set(initCommands, offset)
        offset += initCommands.length
        
        // Add image commands
        finalCommands.set(imageCommandsFlat, offset)
        offset += imageCommandsFlat.length
        
        // Add feed lines
        finalCommands.set(feedCommands, offset)
        
        // For now, skip images in templates since builder interferes
        // Images work perfectly when sent directly, but builder system corrupts them

        // Send image commands via builder

        builder.raw(finalCommands)
        
        // Memory cleanup
        imageCommandsFlat = null
      } else {
        // Still send initialization and feed commands even if no image data

        const feedOnlyCommands = new Uint8Array([0x1B, 0x40, 0x0A, 0x0A]) // Init + feeds
        builder.raw(feedOnlyCommands)
      }
      
      // Force cleanup after successful processing
      if (typeof window !== 'undefined' && window.gc) {
        window.gc()
      }
      
    } catch (error) {
      console.error('Item image generation error:', error)
      // Send just feed commands if error
      const feedOnlyCommands = new Uint8Array([0x1B, 0x40, 0x0A, 0x0A])
      builder.raw(feedOnlyCommands)
    }
  }

  /**
   * Add business name as image (with automatic fallback to sanitized text)
   * Always tries image generation first, falls back to text if it fails
   */
  async addBusinessName(builder, businessName) {
    try {
      console.log(`🏢 Converting business name to image: "${businessName}"`)
      
      // Generate image commands with larger font for business name
      const imageCommands = await defaultTextImageConverter.textToImageCommands(businessName, {
        fontSize: 30, // Larger font for prominent business name (increased from 26)
        maxWidth: (this.charWidth - 2) * 8, // Slightly reduced width to ensure it fits
        wordWrap: true,
        textAlign: 'center', // Center the text within the image
        padding: 3, // Reduced padding for compact output (from default 8)
        lineHeight: 1.2, // Tighter line spacing (from default 1.6)
        compactMode: true // Custom flag for compact rendering
      })
      
      console.log(`📸 Generated ${imageCommands.length} business name image command(s)`)
      
      // Debug: Log the configuration being used
      console.log(`🔍 Business name config: fontSize=32, maxWidth=${this.charWidth * 8}, textAlign=center`)
      
      // Flatten image commands properly (like working custom test)
      let imageCommandsFlat = []
      for (const command of imageCommands) {
        if (Array.isArray(command)) {
          imageCommandsFlat.push(...command)  // Spread array elements
        }
      }
      
      console.log(`🔍 Flattened ${imageCommandsFlat.length} bytes of image data`)
      
      if (imageCommandsFlat.length > 0) {
        // Business name with centering commands
        const centerCommands = [0x1B, 0x61, 0x01] // ESC a 1 - Center alignment
        const resetCommands = [0x1B, 0x61, 0x00] // ESC a 0 - Left alignment  
        const feedCommands = [] // No line feeds - let template control spacing
        
        // Create complete command sequence with centering
        const finalCommands = new Uint8Array(centerCommands.length + imageCommandsFlat.length + resetCommands.length + feedCommands.length)
        let offset = 0
        
        // Add center alignment
        finalCommands.set(centerCommands, offset)
        offset += centerCommands.length
        
        // Add image commands
        finalCommands.set(imageCommandsFlat, offset)
        offset += imageCommandsFlat.length
        
        // Add reset alignment
        finalCommands.set(resetCommands, offset)  
        offset += resetCommands.length
        
        // Add feed lines
        finalCommands.set(feedCommands, offset)
        
        // Send complete sequence through FIXED builder system
        builder.raw(finalCommands)
        console.log(`✅ Business name image printed successfully (${finalCommands.length} bytes total - CENTERED and LARGER)`)
        
        // Memory cleanup
        imageCommandsFlat = null
      } else {
        // No text fallback - image only for business name
        console.log(`⚠️ No business name image data generated - no fallback`)
      }
      
      // Force cleanup after successful processing  
      if (typeof window !== 'undefined' && window.gc) {
        window.gc()
      }
      
    } catch (error) {
      console.error('Business name image generation error:', error)
      // No text fallback - image only for business name
    }
  }

  /**
   * Add image for any text content
   */
  async addTextAsImage(builder, text, options = {}) {
    try {
      console.log(`Attempting image generation for text: "${text}"`)
      
      // TEMPORARY: Skip image generation due to integration issues
      console.warn('Text-to-image temporarily disabled - using sanitized text fallback')
      
      if (options.bold) {
        builder.bold(true)
      }
      
      builder.text(this.sanitizeText(text))
      
      if (options.bold) {
        builder.reset()
      }
      
      if (options.feedAfter !== false) {
        builder.feed()
      }
      
      /* TODO: Fix image command integration
      const imageCommands = await defaultTextImageConverter.textToImageCommands(text, {
        fontSize: options.fontSize || 12,
        maxWidth: options.maxWidth || (this.charWidth * 8),
        wordWrap: options.wordWrap ?? true,
        fontWeight: options.bold ? 'bold' : 'normal'
      })
      
      for (const command of imageCommands) {
        builder.raw(new Uint8Array(command))
      }
      
      if (options.feedAfter !== false) {
        builder.feed()
      }
      */
      
    } catch (error) {
      console.error('Failed to generate image for text:', error)
      // Fallback to sanitized text
      builder.text(this.sanitizeText(text))
    }
  }

  /**
   * Sanitize all text content to prevent encoding issues (fallback)
   */
  sanitizeText(text) {
    return sanitizeText(text)
  }
}

/**
 * Compact Template - Minimal information
 */
class CompactTemplate extends BaseTemplate {
  async addHeader(builder, business) {
    // Business name - now using improved image generation
    if (business.name) {
      builder.align('center')
        .bold(true)
      
      await this.addBusinessName(builder, business.name)
      
      builder.reset()
        .align('left')
    }

    // Business address - compact format
    if (business.address) {
      console.log('📍 CompactTemplate - Business address:', business.address)
      const fullAddress = TextFormatter.formatAddress(business.address)
      console.log('📍 CompactTemplate - Formatted address:', fullAddress)
      if (fullAddress) {
        const addressLines = TextFormatter.wordWrap(fullAddress, this.charWidth)
        builder.align('center')
        for (const line of addressLines) {
          builder.text(line)
        }
        builder.align('left')
      }
    }
    
    builder.separator('=')
  }

  async addCustomerInfo(builder, transaction) {
    // Invoice and date on one line
    const invoice = transaction.invoice_number || transaction._id?.slice(-6) || 'N/A'
    const date = TextFormatter.date(transaction.date || transaction.created_at)
    
    builder.text(TextFormatter.leftRight(
      `Bill: ${invoice}`,
      date,
      this.charWidth
    ))
    
    // Add customer info for compact templates too (if not walk-in)
    if (transaction.customer_name && transaction.customer_name !== 'Walk-in Customer') {
      builder.text(`Customer: ${this.sanitizeText(transaction.customer_name)}`)
      if (transaction.customer_mobile) {
        builder.text(`Mobile: ${transaction.customer_mobile}`)
      }
    }
    
    builder.separator()
  }

  async addItems(builder, transaction, business) {
    // Simple item format: Item Name @price x qty = total (all on one line)
    const items = transaction?.items || []
    
    if (!Array.isArray(items)) {
      console.warn('Transaction items is not an array:', items)
      return
    }
    
    for (const item of items) {
      // Add defensive null/undefined checks
      if (!item) {
        console.warn('Skipping null/undefined item in transaction')
        continue
      }
      
      const name = item.product_name || item.name || 'Item' 
      const unitPrice = item.unit_price || item.price || 0
      const quantity = item.quantity || 1
      const total = item.total_price || (quantity * unitPrice)
      
      // Add item name as image first - ensure name is valid
      if (name && typeof name === 'string' && name.trim()) {
        await this.addItemName(builder, name.trim())
      } else {
        console.warn('Invalid item name, using fallback:', name)
        await this.addItemName(builder, 'Item')
      }
      
      // Add price and quantity info with total right-aligned
      const leftPart = `${TextFormatter.currency(unitPrice)} x ${quantity} =`
      const rightPart = TextFormatter.currency(total)
      
      builder.text(TextFormatter.leftRight(
        leftPart,
        rightPart,
        this.charWidth
      ))
      
      // Add minimal spacing between items (compact)
      builder.feed()
    }
  }

  async addTotals(builder, transaction, business) {
    builder.separator()
    
    // Simple total
    builder.bold(true)
      .text(TextFormatter.leftRight(
        'TOTAL:',
        TextFormatter.currency(transaction.total),
        this.charWidth
      ))
      .reset()
    
    // Payment method
    if (transaction.payment_method) {
      builder.text(TextFormatter.leftRight(
        transaction.payment_method,
        TextFormatter.currency(transaction.total),
        this.charWidth
      ))
    }
  }

  async addFooter(builder, business) {
    builder.separator('=')
      .align('center')
      .text('Thank you!')
  }
}

/**
 * Detailed Template - Complete information with GST
 */
class DetailedTemplate extends BaseTemplate {
  async addHeader(builder, business) {
    // Business name - now using improved image generation
    if (business.name) {
      builder.align('center')
        .bold(true)
        .size(1, 2)
      
      await this.addBusinessName(builder, business.name)
      
      builder.reset()
        .align('left')
    }

    // Business address
    if (business.address) {
      const fullAddress = TextFormatter.formatAddress(business.address)
      if (fullAddress) {
        const addressLines = TextFormatter.wordWrap(fullAddress, this.charWidth)
        builder.align('center')
        for (const line of addressLines) {
          builder.text(line)
        }
      }
    }

    // Contact info
    const contact = []
    if (business.contact?.phone) contact.push(`Ph: ${business.contact.phone}`)
    if (business.contact?.email) contact.push(business.contact.email)
    
    if (contact.length > 0) {
      builder.align('center').text(this.sanitizeText(contact.join(' | ')))
    }

    // GSTIN - only show if GST is enabled and GSTIN exists
    if (business.gst_enabled && business.gstin) {
      builder.align('center')
        .text(`GSTIN: ${business.gstin}`)
    }

    builder.separator('=')
  }

  async addCustomerInfo(builder, transaction) {
    // Invoice and date
    const invoice = transaction.invoice_number || transaction._id?.slice(-6) || 'N/A'
    const date = TextFormatter.date(transaction.date || transaction.created_at)
    
    builder.text(TextFormatter.leftRight(`Bill: ${invoice}`, date, this.charWidth))

    // Customer info
    if (transaction.customer_name && transaction.customer_name !== 'Walk-in Customer') {
      builder.text(`Customer: ${this.sanitizeText(transaction.customer_name)}`)
      
      if (transaction.customer_mobile) {
        builder.text(`Mobile: ${transaction.customer_mobile}`)
      }
    }

    builder.separator()
  }

  async addItems(builder, transaction, business) {
    // Items with detailed info
    const items = transaction?.items || []
    
    if (!Array.isArray(items)) {
      console.warn('Transaction items is not an array:', items)
      return
    }
    
    for (const item of items) {
      // Add defensive null/undefined checks
      if (!item) {
        console.warn('Skipping null/undefined item in transaction')
        continue
      }
      
      const name = item.product_name || item.name || 'Item'
      const qty = item.quantity || 1
      const price = item.unit_price || item.price || 0
      const total = item.total_price || (qty * price)

      // Item name as image - ensure name is valid
      if (name && typeof name === 'string' && name.trim()) {
        await this.addItemName(builder, name.trim())
      } else {
        console.warn('Invalid item name, using fallback:', name)
        await this.addItemName(builder, 'Item')
      }

      // Quantity, rate, and amount
      builder.text(TextFormatter.leftRight(
        `  ${qty} x ${TextFormatter.currency(price)}`,
        TextFormatter.currency(total),
        this.charWidth
      ))

      // GST details if available and GST is enabled
      if (business.gst_enabled && item.gst_rate && item.gst_rate.total > 0) {
        const gstAmount = (total * item.gst_rate.total) / (100 + item.gst_rate.total)
        builder.text(`    (GST ${item.gst_rate.total}%: ${TextFormatter.currency(gstAmount)})`)
      }
      
      // Add minimal spacing between items (compact)
      builder.feed()
    }
  }

  async addTotals(builder, transaction, business) {
    builder.separator()

    // Subtotal if available and GST is enabled
    if (business.gst_enabled && transaction.subtotal && transaction.gst_details) {
      builder.text(TextFormatter.leftRight(
        'Subtotal:',
        TextFormatter.currency(transaction.subtotal),
        this.charWidth
      ))

      // GST breakdown
      const gst = transaction.gst_details
      if (gst.cgst > 0) {
        builder.text(TextFormatter.leftRight(
          'CGST:',
          TextFormatter.currency(gst.cgst),
          this.charWidth
        ))
      }
      if (gst.sgst > 0) {
        builder.text(TextFormatter.leftRight(
          'SGST:',
          TextFormatter.currency(gst.sgst),
          this.charWidth
        ))
      }
      if (gst.igst > 0) {
        builder.text(TextFormatter.leftRight(
          'IGST:',
          TextFormatter.currency(gst.igst),
          this.charWidth
        ))
      }
    }

    // Total
    builder.separator()
      .bold(true)
      .size(1, 2)
      .text(TextFormatter.leftRight(
        'TOTAL:',
        TextFormatter.currency(transaction.total),
        this.charWidth
      ))
      .reset()

    // Payment method
    if (transaction.payment_method) {
      builder.text(TextFormatter.leftRight(
        'Payment:',
        transaction.payment_method,
        this.charWidth
      ))
    }
  }

  async addFooter(builder, business) {
    builder.separator('=')
      .align('center')
      .text('Thank you for your business!')
      .feed()
      .text('Powered by QuickBill POS')
  }
}

/**
 * Columnized Template - Table format with aligned columns
 */
class ColumnizedTemplate extends BaseTemplate {
  async addHeader(builder, business) {
    // Same as detailed template
    const detailedTemplate = new DetailedTemplate(this.config)
    await detailedTemplate.addHeader(builder, business)
  }

  async addCustomerInfo(builder, transaction) {
    // Same as detailed template
    const detailedTemplate = new DetailedTemplate(this.config)
    await detailedTemplate.addCustomerInfo(builder, transaction)
  }

  async addItems(builder, transaction, business) {
    // Calculate column widths based on paper size
    let nameWidth, qtyWidth, rateWidth, amountWidth

    if (this.charWidth <= 32) {
      // 58mm layout
      nameWidth = 12
      qtyWidth = 3
      rateWidth = 6
      amountWidth = 7
    } else {
      // 80mm layout
      nameWidth = 20
      qtyWidth = 4
      rateWidth = 8
      amountWidth = 10
    }

    // Header row with updated layout
    builder.bold(true)
      .text('ITEM NAME')
      .text(
        TextFormatter.left('MRP', rateWidth) + '|' +
        TextFormatter.left('RATE', rateWidth) + '|' +
        TextFormatter.left('QTY', qtyWidth) + '|' +
        TextFormatter.left('TAX', rateWidth) + '|' +
        TextFormatter.left('TOTAL', amountWidth)
      )
      .reset()
      .separator()

    // Items
    const items = transaction?.items || []
    
    if (!Array.isArray(items)) {
      console.warn('Transaction items is not an array:', items)
      return
    }
    
    for (const item of items) {
      // Add defensive null/undefined checks
      if (!item) {
        console.warn('Skipping null/undefined item in transaction')
        continue
      }
      
      const name = item.product_name || item.name || 'Item'
      const qty = item.quantity || 1
      const price = item.unit_price || item.price || 0
      const mrp = item.mrp || price // Use MRP if available, otherwise use price
      const total = item.total_price || (qty * price)
      const gstRate = item.gst_rate ? item.gst_rate.total : 0
      const gstAmount = gstRate > 0 ? (total * gstRate) / (100 + gstRate) : 0

      // Add item name as image (First Line) - ensure name is valid
      if (name && typeof name === 'string' && name.trim()) {
        await this.addItemName(builder, name.trim())
      } else {
        console.warn('Invalid item name, using fallback:', name)
        await this.addItemName(builder, 'Item')
      }
      
      // Add item details in columnized format (Second Line)
      builder.text(
        TextFormatter.left(`Rs${mrp}`, rateWidth) + '|' +
        TextFormatter.left(`Rs${price}`, rateWidth) + '|' +
        TextFormatter.left(qty.toString(), qtyWidth) + '|' +
        TextFormatter.left(gstRate > 0 ? `${gstRate}%` : '0%', rateWidth) + '|' +
        TextFormatter.left(TextFormatter.currency(total), amountWidth)
      )

      // Add minimal spacing between items
      builder.feed()
    }
  }

  async addTotals(builder, transaction, business) {
    builder.separator()

    // Calculate column widths for totals section
    const labelWidth = this.charWidth - 10
    const amountWidth = 10

    // Subtotal and GST - only if GST is enabled
    if (business.gst_enabled && transaction.subtotal && transaction.gst_details) {
      builder.text(TextFormatter.leftRight(
        'Subtotal:',
        TextFormatter.currency(transaction.subtotal),
        this.charWidth
      ))

      const gst = transaction.gst_details
      if (gst.cgst > 0) {
        builder.text(TextFormatter.leftRight(
          'CGST:',
          TextFormatter.currency(gst.cgst),
          this.charWidth
        ))
      }
      if (gst.sgst > 0) {
        builder.text(TextFormatter.leftRight(
          'SGST:',
          TextFormatter.currency(gst.sgst),
          this.charWidth
        ))
      }
      if (gst.igst > 0) {
        builder.text(TextFormatter.leftRight(
          'IGST:',
          TextFormatter.currency(gst.igst),
          this.charWidth
        ))
      }
    }

    // Total
    builder.separator()
      .bold(true)
      .text(TextFormatter.leftRight(
        'TOTAL:',
        TextFormatter.currency(transaction.total),
        this.charWidth
      ))
      .reset()
  }

  async addFooter(builder, business) {
    builder.separator('=')
      .align('center')
      .text('Thank you!')
  }
}

/**
 * Template Engine - Main class that manages all templates
 */
export class ReceiptTemplateEngine {
  constructor() {
    this.templates = new Map()
    this.initialize()
  }

  /**
   * Initialize all available templates
   */
  initialize() {
    // Register all template variants
    this.templates.set('compact-58', new CompactTemplate(TEMPLATE_CONFIGS['compact-58']))
    this.templates.set('compact-80', new CompactTemplate(TEMPLATE_CONFIGS['compact-80']))
    this.templates.set('detailed-58', new DetailedTemplate(TEMPLATE_CONFIGS['detailed-58']))
    this.templates.set('detailed-80', new DetailedTemplate(TEMPLATE_CONFIGS['detailed-80']))
    this.templates.set('columnized-58', new ColumnizedTemplate(TEMPLATE_CONFIGS['columnized-58']))
    this.templates.set('columnized-80', new ColumnizedTemplate(TEMPLATE_CONFIGS['columnized-80']))
  }

  /**
   * Get available template configurations
   */
  getAvailableTemplates() {
    return Object.values(TEMPLATE_CONFIGS)
  }

  /**
   * Get templates by category
   */
  getTemplatesByCategory(category) {
    return Object.values(TEMPLATE_CONFIGS).filter(config => config.category === category)
  }

  /**
   * Get templates by paper width
   */
  getTemplatesByPaperWidth(paperWidth) {
    return Object.values(TEMPLATE_CONFIGS).filter(config => config.paperWidth === paperWidth)
  }

  /**
   * Get a specific template
   */
  getTemplate(templateId) {
    if (!this.templates.has(templateId)) {
      throw new Error(`Template '${templateId}' not found`)
    }
    return this.templates.get(templateId)
  }

  /**
   * Generate receipt using specified template
   */
  async generateReceipt(templateId, transaction, business, options = {}) {
    const template = this.getTemplate(templateId)
    return await template.generate(transaction, business, options)
  }

  /**
   * Validate template compatibility with paper width
   */
  validateTemplate(templateId, paperWidth) {
    const config = TEMPLATE_CONFIGS[templateId]
    if (!config) {
      throw new Error(`Template '${templateId}' not found`)
    }
    
    if (config.paperWidth !== paperWidth) {
      throw new Error(`Template '${templateId}' is not compatible with ${paperWidth}mm paper width`)
    }
    
    return true
  }

  /**
   * Get recommended template for given constraints
   */
  getRecommendedTemplate(paperWidth, category = 'detailed') {
    const templates = this.getTemplatesByPaperWidth(paperWidth)
    const categoryTemplates = templates.filter(t => t.category === category)
    
    if (categoryTemplates.length > 0) {
      return categoryTemplates[0].id
    }
    
    if (templates.length > 0) {
      return templates[0].id
    }
    
    // Fallback to any available template
    return Object.keys(TEMPLATE_CONFIGS)[0]
  }
}

// Create singleton instance
export const templateEngine = new ReceiptTemplateEngine()

// Export template classes for advanced usage
export {
  BaseTemplate,
  CompactTemplate,
  DetailedTemplate,
  ColumnizedTemplate,
  TextFormatter
}